import React, { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Card } from 'components/Card'
import { Heading } from 'components/Heading'
import { Stack } from 'components/Stack'
import { Text } from 'components/Text'
import { TextInput } from 'components/TextInput'
import { Button } from 'components/Button'
import { Alert } from 'components/Alert'
import { useFormFields } from 'hooks/useFormFields'
import { useUpdatePassword } from 'services/user'

export const ChangePasswordForm: FC = () => {
  const { t } = useTranslation()
  const [error, setError] = useState<string | null>()
  const [updatePassword, { status }] = useUpdatePassword()
  const [form, onFieldChange] = useFormFields({
    currentPassword: '',
    password: '',
    passwordConfirmation: '',
  })

  const onFormSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    setError(null)

    if (form.password !== form.passwordConfirmation) {
      setError(t('account.change_password.form.error_passwords_match'))
      return
    }

    updatePassword(form, {
      onError() {
        setError(t('account.change_password.form.error_submit'))
      },
    })
  }

  return (
    <Card>
      <Stack>
        <Stack space="small">
          <Heading size="card">{t('account.change_password.heading')}</Heading>
          <Text>{t('account.change_password.subtitle')}</Text>
        </Stack>

        <form onSubmit={onFormSubmit}>
          <Stack space="medium">
            <TextInput
              name="currentPassword"
              type="password"
              value={form.currentPassword}
              onChange={onFieldChange}
              label={t('account.change_password.form.current')}
              required
              autoComplete="new-password"
            />

            <TextInput
              name="password"
              type="password"
              value={form.password}
              onChange={onFieldChange}
              label={t('account.change_password.form.password')}
              required
            />

            <TextInput
              name="passwordConfirmation"
              type="password"
              value={form.passwordConfirmation}
              onChange={onFieldChange}
              label={t('account.change_password.form.confirmation')}
              required
            />
            <Button type="submit" loading={status === 'loading'}>
              {t('account.change_password.form.cta')}
            </Button>

            {error && (
              <Alert tone="critical" full>
                {error}
              </Alert>
            )}

            {status === 'success' && (
              <Alert tone="positive" full>
                {t('account.change_password.success')}
              </Alert>
            )}
          </Stack>
        </form>
      </Stack>
    </Card>
  )
}

ChangePasswordForm.displayName = 'ChangePasswordForm'
